import { useState } from "react";
import { Instagram, MapPin, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { site } from "../data/siteConfig";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../hooks/useTitle";

const inputClass =
  "w-full rounded-xl border border-white/10 bg-smoke px-4 py-3.5 text-sm text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-hotpink";

export default function ContactPage() {
  useTitle("Contact | HairByYaya");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    toast.success("Message sent — prototype preview, no email was delivered.");
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="px-5 pb-24 pt-28 md:pt-36" data-testid="contact-page">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="font-script text-3xl text-hotpink">say hello</p>
          <h1 className="mt-2 font-serif text-4xl md:text-6xl">Contact</h1>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-2">
          <Reveal className="space-y-5">
            <a
              href={site.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="card-glow-hover flex items-center gap-5 rounded-3xl border border-white/10 bg-coal p-6"
              data-testid="contact-instagram-card"
            >
              <img
                src={site.assets.qr}
                alt="Instagram QR code for @hairbyyaya_didthat"
                className="h-24 w-24 rounded-2xl border border-white/15"
              />
              <div>
                <p className="flex items-center gap-2 font-serif text-xl">
                  <Instagram className="h-5 w-5 text-hotpink" /> Instagram
                </p>
                <p className="mt-1 text-sm text-white/60">
                  {site.instagram.handle}
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-hotpink">
                  Fastest way to reach me
                </p>
              </div>
            </a>

            <div className="rounded-3xl border border-white/10 bg-coal p-6">
              <p className="flex items-center gap-2 font-serif text-xl">
                <MapPin className="h-5 w-5 text-hotpink" /> Hampton, Virginia
              </p>
              <p className="mt-2 text-sm leading-relaxed text-white/60">
                {site.locationNote}
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-coal p-6">
              <p className="flex items-center gap-2 font-serif text-xl">
                <Mail className="h-5 w-5 text-hotpink" /> Email
              </p>
              <p className="mt-2 text-sm text-white/60">{site.email}</p>
              <p className="mt-2 text-[11px] text-white/30">
                Placeholder — business email to be confirmed by the owner.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form
              onSubmit={submit}
              className="rounded-3xl border border-white/10 bg-coal p-7"
              data-testid="contact-form"
            >
              <h2 className="font-serif text-2xl">Send a message</h2>
              <div className="mt-6 space-y-5">
                <label className="block">
                  <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                    Name
                  </span>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                    data-testid="contact-name"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                    Email
                  </span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@email.com"
                    autoComplete="email"
                    data-testid="contact-email"
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
                    Message
                  </span>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Ask about a style, a date, or anything else…"
                    data-testid="contact-message"
                    className={inputClass}
                  />
                </label>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-hotpink py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-magenta"
                >
                  <Send className="h-4 w-4" /> Send Message
                </button>
                <p className="text-center text-[11px] text-white/30">
                  Prototype form — messages are not delivered yet.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

